import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout, { SEOHead } from '../../components/PublicLayout';
import { Button } from '../../components/ui/button';
import { CheckCircle, Zap, ArrowRight, Coins, Bot, Headphones, Info, CreditCard, Package } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PricingPage() {
  const [settings, setSettings] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/credit-settings`).then(r => r.json()),
      fetch(`${API}/api/pricing/bundles`).then(r => r.json()),
      fetch(`${API}/api/pricing/tiers`).then(r => r.json()),
    ]).then(([s, b, t]) => {
      setSettings(s);
      setBundles(Array.isArray(b) ? b : []);
      setTiers(Array.isArray(t) ? t : []);
    }).catch(() => {
      setSettings({ base_rate_cents: 10, ai_credits_per_minute: 4, human_credits_per_minute: 16 });
    });
  }, []);

  const baseRate = settings?.base_rate_cents || 10;
  const aiTokens = settings?.ai_credits_per_minute || 4;
  const humanTokens = settings?.human_credits_per_minute || 16;
  const aiPricePerMin = ((baseRate * aiTokens) / 100).toFixed(2);
  const humanPricePerMin = ((baseRate * humanTokens) / 100).toFixed(2);
  const tokenPriceDollars = (baseRate / 100).toFixed(2);

  return (
    <PublicLayout>
      <SEOHead title="Pricing | Interpreter Services Plans & Rates | Interpreter Services" description="Token-based pricing for AI interpreting. Subscriptions, pay-as-you-go, and token bundles. Human escalation premium. Enterprise custom plans." canonical="https://interpreterservices.us/pricing" />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">Plans & Pricing</h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">Simple, transparent pricing designed for organizations of every size. Choose a subscription or pay as you go.</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-16 space-y-20">

        {/* ─── SECTION 1: Subscription Options ─── */}
        <section data-testid="subscription-section">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Subscription Options</h2>
            <p className="text-slate-500 max-w-xl mx-auto">Get a monthly allocation of tokens at the best rates. Upgrade or downgrade anytime.</p>
          </div>
          {tiers.length > 0 && (
            <div className={`grid gap-8 ${tiers.length === 1 ? 'max-w-sm mx-auto' : tiers.length === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' : 'md:grid-cols-3'}`}>
              {tiers.map((t, i) => {
                const isMostPopular = i === 1 && tiers.length >= 3;
                const monthlyPrice = (t.price_cents_per_month / 100);
                const aiMins = Math.floor(t.credits_per_month / aiTokens);
                const humanMins = Math.floor(t.credits_per_month / humanTokens);
                return (
                  <div key={t.id} className={`rounded-xl p-8 relative ${isMostPopular ? 'border-2 border-blue-600 shadow-lg ring-1 ring-blue-600 bg-white' : 'border bg-white'}`} data-testid={`tier-card-${t.id}`}>
                    {isMostPopular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full">Most Popular</div>}
                    <h3 className="text-lg font-bold mb-2">{t.name}</h3>
                    <div className="text-4xl font-bold mb-1">
                      {monthlyPrice === 0 ? '$0' : `$${monthlyPrice.toFixed(0)}`}
                      <span className="text-lg font-normal text-slate-400">/mo</span>
                    </div>
                    <p className="text-sm text-slate-500 mb-2">{t.credits_per_month} tokens/month</p>
                    <p className="text-xs text-slate-400 mb-6">={aiMins} min AI or {humanMins} min human</p>
                    <a href="/auth?tab=register" target="_blank" rel="noopener noreferrer">
                      <Button className={`w-full mb-6 ${isMostPopular ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`} variant={isMostPopular ? 'default' : 'outline'} data-testid={`tier-signup-${t.id}`} style={{display:'none'}}>
                        {monthlyPrice === 0 ? 'Start Free' : 'Sign Up'} <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </a>
                    <ul className="space-y-2.5">
                      {(t.features || []).map(f => (
                        <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />{f}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Divider */}
        <div className="border-t" />

        {/* ─── SECTION 2: Pay As You Go ─── */}
        <section data-testid="payg-section">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Pay As You Go</h2>
            <p className="text-slate-500 max-w-xl mx-auto">No commitment. Pay per minute of interpreting. Choose AI for speed or human for high-stakes situations.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* AI Pay-as-you-go */}
            <div className="rounded-xl border-2 border-blue-600 bg-white p-8 shadow-sm" data-testid="payg-ai">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold">AI Interpreting</h3>
                  <p className="text-xs text-slate-400">Instant access, 250+ languages</p>
                </div>
              </div>
              <div className="text-4xl font-bold mb-1" data-testid="payg-ai-price">${aiPricePerMin}<span className="text-lg font-normal text-slate-400">/min</span></div>
              <p className="text-sm text-slate-500 mb-6">{aiTokens} tokens per minute</p>
              <a href="/auth?tab=register" target="_blank" rel="noopener noreferrer"><Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="payg-ai-signup" style={{display:'none'}}>Sign Up <ArrowRight className="w-4 h-4 ml-1" /></Button></a>
              <ul className="mt-6 space-y-2.5">
                {['Instant AI interpreting', '250+ languages supported', 'No minimum commitment', 'Full session reporting', 'Admin dashboard & API access'].map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
            </div>
            {/* Human Pay-as-you-go */}
            <div className="rounded-xl border bg-white p-8" data-testid="payg-human">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center">
                  <Headphones className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold">Human Interpreting</h3>
                  <p className="text-xs text-slate-400">Premium, SLA-backed</p>
                </div>
              </div>
              <div className="text-4xl font-bold mb-1" data-testid="payg-human-price">${humanPricePerMin}<span className="text-lg font-normal text-slate-400">/min</span></div>
              <p className="text-sm text-slate-500 mb-6">{humanTokens} tokens per minute</p>
              <a href="/auth?tab=register" target="_blank" rel="noopener noreferrer"><Button className="w-full" variant="outline" data-testid="payg-human-signup" style={{display:'none'}}>Sign Up <ArrowRight className="w-4 h-4 ml-1" /></Button></a>
              <ul className="mt-6 space-y-2.5">
                {['Professional human interpreters', 'Seamless AI-to-human handoff', 'SLA-backed response times', 'Legal & medical specialists', 'On-demand and pre-scheduled'].map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t" />

        {/* ─── SECTION 3: Token Bundles ─── */}
        {bundles.length > 0 && (
        <section data-testid="bundles-section">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Token Bundles</h2>
            <p className="text-slate-500 max-w-xl mx-auto">Purchase tokens in advance at discounted rates. Tokens never expire.</p>
          </div>
          <div className={`grid gap-6 max-w-4xl mx-auto ${bundles.length === 1 ? 'max-w-sm' : bundles.length === 2 ? 'sm:grid-cols-2 max-w-2xl' : 'sm:grid-cols-3'}`}>
            {bundles.map(b => {
              const effectivePrice = ((b.computed_price_cents ?? b.price_cents) / 100);
              const perToken = (effectivePrice / b.credits);
              const aiMins = Math.floor(b.credits / aiTokens);
              const humanMins = Math.floor(b.credits / humanTokens);
              return (
                <div key={b.id} className="rounded-xl border bg-white p-6 text-center relative" data-testid={`bundle-card-${b.id}`}>
                  {b.discount_percent > 0 && (
                    <div className="absolute -top-2.5 right-4 bg-green-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full">Save {b.discount_percent}%</div>
                  )}
                  <p className="font-bold text-lg mb-2">{b.name}</p>
                  <p className="text-3xl font-bold text-slate-900 mb-1">${effectivePrice.toFixed(2)}</p>
                  <p className="text-xs text-slate-500 mb-2">{b.credits} tokens &middot; ${perToken.toFixed(2)}/token</p>
                  <p className="text-xs text-slate-400 mb-4">={aiMins} min AI &middot; {humanMins} min human</p>
                  <Link to="/contact"><Button variant="outline" size="sm" className="w-full" data-testid={`bundle-signup-${b.id}`}>Purchase</Button></Link>
                </div>
              );
            })}
          </div>
        </section>
        )}

        {/* Divider */}
        <div className="border-t" />

        {/* ─── SECTION 4: How Token Pricing Works ─── */}
        <section data-testid="token-explainer-section">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">How Token Pricing Works</h2>
            <p className="text-slate-500 max-w-xl mx-auto">All interpreting usage is measured in tokens. Here's how the system works.</p>
          </div>

          <div className="max-w-3xl mx-auto space-y-8">
            {/* Rate Cards */}
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-100 text-center">
                <Coins className="w-6 h-6 text-blue-600 mx-auto mb-3" />
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Token Value</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="token-price">${tokenPriceDollars}</p>
                <p className="text-xs text-slate-500 mt-1">per token</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-100 text-center">
                <Bot className="w-6 h-6 text-blue-600 mx-auto mb-3" />
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">AI Interpreting</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="ai-price">${aiPricePerMin}<span className="text-sm font-normal text-slate-500">/min</span></p>
                <p className="text-xs text-slate-500 mt-1">{aiTokens} tokens per minute</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-100 text-center">
                <Headphones className="w-6 h-6 text-emerald-600 mx-auto mb-3" />
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Human Interpreting</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="human-price">${humanPricePerMin}<span className="text-sm font-normal text-slate-500">/min</span></p>
                <p className="text-xs text-slate-500 mt-1">{humanTokens} tokens per minute</p>
              </div>
            </div>

            {/* Step-by-step explanation */}
            <div className="space-y-4">
              {[
                { num: '1', title: 'Tokens are your interpreting currency', desc: `Each token is worth $${tokenPriceDollars}. You acquire tokens through a subscription, a one-time bundle purchase, or pay-as-you-go billing.` },
                { num: '2', title: 'Tokens are consumed per minute', desc: `AI interpreting uses ${aiTokens} tokens/min ($${aiPricePerMin}/min). Human interpreting uses ${humanTokens} tokens/min ($${humanPricePerMin}/min). Tokens are deducted automatically as you use interpreting services.` },
                { num: '3', title: 'Volume discounts reward larger purchases', desc: 'Token bundles offer up to 25% savings compared to the standard rate. Subscription plans include monthly token allocations at the best per-token value.' },
                { num: '4', title: 'Tokens never expire', desc: 'Purchased tokens remain in your account until used. Subscription tokens reset monthly but purchased bundles carry forward indefinitely.' },
              ].map(s => (
                <div key={s.num} className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-bold shrink-0">{s.num}</div>
                  <div>
                    <p className="font-semibold text-slate-900">{s.title}</p>
                    <p className="text-sm text-slate-500 mt-0.5">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Enterprise CTA */}
        <div className="bg-slate-900 rounded-2xl p-10 text-white text-center">
          <h2 className="text-2xl font-bold mb-4">Need a Custom Plan?</h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-8">Enterprise organizations get custom token pricing, SLA guarantees, dedicated support, SSO, and advanced analytics.</p>
          <div className="grid sm:grid-cols-3 gap-6 mb-8 text-left max-w-3xl mx-auto">
            {['SLA guarantees', 'Dedicated account management', 'Custom policy controls', 'SSO integration', 'Advanced reporting & analytics', 'Priority human escalation'].map(f => (
              <div key={f} className="flex items-start gap-2 text-sm text-slate-300"><CheckCircle className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />{f}</div>
            ))}
          </div>
          <Link to="/contact"><Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-10">Talk to Sales</Button></Link>
        </div>
      </div>

      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold mb-4">Questions About Pricing?</h2>
          <p className="text-slate-600 mb-8">Our team can help you find the right plan for your organization's needs and volume.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact"><Button className="bg-blue-600 hover:bg-blue-700 text-white px-8">Contact Sales</Button></Link>
            <Link to="/book-demo"><Button variant="outline" className="text-blue-700 border-blue-700 hover:bg-blue-50 px-8">Book Demo</Button></Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
